import React, { FC, useEffect, useState } from 'react';
import { dispatch, useSelector } from 'store';

import { CircularProgress, Grid, Skeleton, TextField } from '@mui/material';

import { onFetchSimulations, onEditSimulation, onOpenEditAlert } from 'store/slices/simulation';
import { openSnackbar } from 'store/slices/snackbar';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import FormDialog from 'ui-component/dialogs/FormDialog';
import nameof from 'utils/form';
import { Simulation } from 'models/Simulation';
import SimulationService from 'services/SimulationService';

interface Error {
    field: string;
    message: string;
}

const EditSimulation: FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const { openEdit, simulationId } = useSelector((state) => state.simulation);

    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [newSimulation, setNewSimulation] = useState<Simulation>({ id: '', name: '', note: '' });

    const simulationService = new SimulationService();

    const handleCancel = () => {
        dispatch(onOpenEditAlert(false));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        setNewSimulation((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = async () => {
        if (!user) return false;

        if (!newSimulation.name) {
            setError({ field: nameof<Simulation>('name'), message: 'SIMULATION_NAME_IS_REQUIRED' });
            return false;
        }

        setIsLoading(true);
        try {
            const res = await dispatch(onEditSimulation(user.tenantId, user.userId, user.userName, newSimulation));
            if (res) {
                dispatch(onOpenEditAlert(false));
                dispatch(onFetchSimulations(user.tenantId));
                dispatch(
                    openSnackbar({
                        open: true,
                        message: t('VALUE_UPDATED_SUCCESSFULLY'),
                        variant: 'alert',
                        alert: {
                            color: 'success'
                        },
                        close: false
                    })
                );
            } else {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: t('VALUE_UPDATE_FAILED'),
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        },
                        close: false
                    })
                );
            }
            setIsLoading(false);
            return res;
        } catch (error) {
            console.log(error);
            setIsLoading(false);
            return false;
        }
    };

    useEffect(() => {
        (async () => {
            if (user && openEdit) {
                try {
                    setIsLoading(true);
                    const simulations = await simulationService.getSimulationsItems(user.tenantId);
                    const simulation = simulations.find((s) => s.id === simulationId);
                    if (simulation) setNewSimulation(simulation);
                    setIsLoading(false);
                } catch (error) {
                    console.log(error);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, openEdit]);

    return (
        <FormDialog
            showActions
            open={openEdit}
            dialogTitle="Modifica la simulazione"
            contentDescription="Per modificare la simulazione, cambia il nome qui sotto oppure la descrizione e continua."
            onClose={handleCancel}
            onSubmit={handleEdit}
            submitButtonProps={{
                children: isLoading ? (
                    <CircularProgress color="inherit" sx={{ width: '20px !important', height: '20px !important' }} />
                ) : (
                    'Modifica'
                ),
                variant: 'contained'
            }}
            cancelButtonProps={{ children: 'Indietro' }}
            form={
                <Grid container spacing={1} mt={1} direction="column">
                    <Grid item xs zeroMinWidth>
                        {isLoading ? (
                            <Skeleton variant="rectangular" animation="wave" height={45} />
                        ) : (
                            <TextField
                                autoFocus
                                required
                                margin="dense"
                                id="name"
                                name="name"
                                label="Nome"
                                fullWidth
                                variant="outlined"
                                autoComplete="off"
                                value={newSimulation.name}
                                error={!!error && Boolean(error.field)}
                                helperText={error && error.message && t(error.message)}
                                onChange={handleChange}
                            />
                        )}
                    </Grid>
                    <Grid item xs zeroMinWidth>
                        {isLoading ? (
                            <Skeleton variant="rectangular" animation="wave" height={45} />
                        ) : (
                            <TextField
                                multiline
                                margin="dense"
                                id="note"
                                name="note"
                                label="Note"
                                fullWidth
                                variant="outlined"
                                autoComplete="off"
                                value={newSimulation.note}
                                onChange={handleChange}
                            />
                        )}
                    </Grid>
                </Grid>
            }
        />
    );
};

export default EditSimulation;
